/**
 * components/accounting/ExpenseForm.tsx
 * Form to add a new expense with category, amount, payment date, and payment method.
 * - Locked when selected month is closed.
 * - Entry users limited to current month date range.
 * - Viewer: form appears but is fully disabled (read-only).
 */

import { useState, useMemo, use } from 'react'
import { toast } from 'sonner'
import { useSelectedMonth } from '../../hooks/useMonth'
import { useAccountingStore as useAcc } from '../../store/accounting'
import { useUserStore } from '@/store/user'

/**
 * ExpenseForm
 * Adds new expense and previews amount to be deducted.
 * Viewer: UI visible but disabled.
 */
export function ExpenseForm() {

  const addExpense = useUserStore((s) => s.addExpense)
  const gateways = useUserStore((s) => s.gateways)

  const { month, range } = useSelectedMonth()
  const getClosure = useAcc((s) => s.getClosure)
  const isClosed = Boolean(getClosure(month))

  const role = useUserStore((s) => s.role)
  const isAdmin = role === 'admin'
  const isViewer = role === 'viewer'

  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('المصنع')
  const [amount, setAmount] = useState<string>('')

  const defaultDate = useMemo(() => {
    const d = new Date()
    if (!isAdmin) {
      const start = new Date(range.start)
      const end = new Date(range.end)
      if (d < start) return start.toISOString().slice(0, 10)
      if (d > end) return end.toISOString().slice(0, 10)
    }
    return d.toISOString().slice(0, 10)
  }, [isAdmin, range.start, range.end])

  const [paymentDate, setPaymentDate] = useState<string>(defaultDate)
  const [paymentMethod, setPaymentMethod] = useState<string>('تحويل بنكي')

  const gatewayNames = useMemo(() => gateways.map((g) => g.name), [gateways])
  const paymentMethodOptions = useMemo(() => {
    const base = ['تحويل بنكي', 'نقدًا', 'شيك', 'بطاقة']
    const merged = [...base, ...gatewayNames]
    return Array.from(new Set(merged))
  }, [gatewayNames])

  const amountNum = Number(amount || 0)
  const validAmount = Math.max(0, Number.isFinite(amountNum) ? amountNum : 0)

  // Disable form if month closed OR viewer role
  const disabled = isClosed || isViewer

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (disabled) {
      const msg = isViewer ? 'لديك صلاحية مشاهدة فقط' : 'هذا الشهر مُقفَل. افتح الشهر لإضافة مصروفات.'
      toast.error(msg)
      return
    }
    if (!isAdmin) {
      const d = new Date(`${paymentDate}T00:00:00`)
      if (d < new Date(range.start) || d > new Date(range.end)) {
        toast.error('يمكن الإدخال ضمن الشهر الحالي فقط')
        return
      }
    }
    const amt = Number(amount)
    if (!paymentDate) {
      toast.error('الرجاء تحديد تاريخ الدفع')
      return
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('الرجاء إدخال مبلغ صحيح')
      return
    }

    addExpense({
      description,
      category,
      amount: amt,
      payment_date: paymentDate,
      payment_method: paymentMethod,
    })

    toast.success('تمت إضافة المصروف بنجاح')
    setDescription('')
    setAmount('')
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {isClosed && (
        <div className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800">هذا الشهر مُقفَل ولا يمكن إضافة مصروفات.</div>
      )}
      {isViewer && (
        <div className="rounded-lg bg-slate-50 p-2 text-sm text-slate-700">وضع مشاهدة فقط — النموذج معطّل.</div>
      )}
      <div>
        <label className="mb-1 block text-sm text-slate-600">الوصف</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="مثال: دفعة للمصنع"
          disabled={disabled}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <label className="mb-1 block text-sm text-slate-600">التصنيف</label>
          <input
            list="expense-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="المصنع، الشحن، ..."
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <datalist id="expense-categories">
            <option value="المصنع" />
            <option value="الشحن" />
            <option value="رواتب" />
            <option value="إيجار" />
            <option value="خدمات" />
          </datalist>
        </div>

        <div className="sm:col-span-1">
          <label className="mb-1 block text-sm text-slate-600">تاريخ الدفع</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            disabled={disabled}
            min={!isAdmin ? new Date(range.start).toISOString().slice(0, 10) : undefined}
            max={!isAdmin ? new Date(range.end).toISOString().slice(0, 10) : undefined}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <div className="sm:col-span-1">
          <label className="mb-1 block text-sm text-slate-600">طريقة الدفع</label>
          <input
            list="expense-payment-methods"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            placeholder="تحويل بنكي، نقدًا، ..."
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <datalist id="expense-payment-methods">
            {paymentMethodOptions.map((pm) => (
              <option key={pm} value={pm} />
            ))}
          </datalist>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm text-slate-600">المبلغ</label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 disabled:opacity-60 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm text-slate-600">سيُخصم</label>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900">
            <span className="text-slate-700">من الحساب عند الدفع</span>
            <span className="font-semibold">
              {validAmount.toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ريال
            </span>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={disabled}
          className="w-full rounded-lg bg-rose-600 px-4 py-2 font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-rose-300"
        >
          إضافة المصروف
        </button>
      </div>
    </form>
  )
}
